/**
 * React Query Client Configuration
 * Centralized configuration for all data fetching with caching and offline support
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Create a new QueryClient with optimized settings for the ABA platform
 * - Stale time: 5 minutes for most data
 * - Retry: 3 attempts with exponential backoff
 * - Refetch on window focus for fresh data
 * - Background refetch for seamless updates
 */
export const createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Data is considered fresh for 5 minutes
        staleTime: 5 * 60 * 1000,
        // Cache data for 30 minutes
        gcTime: 30 * 60 * 1000,
        // Retry failed requests 3 times with exponential backoff
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Refetch on window focus (good for keeping data fresh)
        refetchOnWindowFocus: true,
        // Don't refetch on reconnect automatically (we handle offline sync separately)
        refetchOnReconnect: false,
        // Refetch when component mounts if data is stale
        refetchOnMount: true,
        // Network mode: always try to fetch, then fallback to cache
        networkMode: 'offlineFirst'
      },
      mutations: {
        // Only retry a mutation the server never answered. If it replied at all
        // — 4xx or 5xx — the write was received and judged, and repeating a
        // non-idempotent POST does harm: a retried create-user attempts the same
        // user twice and the caller is then shown the retry's 409 instead of the
        // real failure, which hides the actual cause. A NetworkError means no
        // reply came back, so one repeat is worth it.
        retry: (failureCount, error) => Boolean(error?.isNetworkError) && failureCount < 1,
        // Network mode for mutations
        networkMode: 'offlineFirst'
      }
    }
  });
};

// Singleton query client instance
let queryClientInstance = null;

/**
 * Get the singleton QueryClient instance
 * Creates one if it doesn't exist
 */
export const getQueryClient = () => {
  if (!queryClientInstance) {
    queryClientInstance = createQueryClient();
  }
  return queryClientInstance;
};

/**
 * Query keys for consistent caching across the application
 * Use these keys to ensure proper cache invalidation
 */
export const queryKeys = {
  // Auth & User
  currentUser: ['currentUser'],
  users: (filters) => ['users', filters],
  user: (id) => ['users', id],

  // Organizations
  organizations: (filters) => ['organizations', filters],
  organization: (id) => ['organizations', id],
  organizationSettings: (orgId) => ['organizations', orgId, 'settings'],

  // Clinical Centers
  clinicalCenters: (filters) => ['clinicalCenters', filters],
  clinicalCenter: (id) => ['clinicalCenters', id],

  // Clients
  clients: (filters) => ['clients', filters],
  client: (id) => ['clients', id],
  clientPrograms: (clientId) => ['clients', clientId, 'programs'],
  clientAssessments: (clientId) => ['clients', clientId, 'assessments'],
  clientDocuments: (clientId) => ['clients', clientId, 'documents'],

  // Programs
  programs: (filters) => ['programs', filters],
  program: (id) => ['programs', id],
  programTargets: (programId) => ['programs', programId, 'targets'],

  // Sessions
  sessions: (filters) => ['sessions', filters],
  session: (id) => ['sessions', id],
  sessionData: (sessionId) => ['sessions', sessionId, 'data'],
  therapistSessions: (therapistId, date) => ['sessions', 'therapist', therapistId, date],

  // Data Collection
  dataEntries: (filters) => ['dataEntries', filters],
  dataEntry: (id) => ['dataEntries', id],
  trialData: (targetId, filters) => ['trialData', targetId, filters],

  // Assessments
  assessments: (filters) => ['assessments', filters],
  assessment: (id) => ['assessments', id],
  assessmentCatalogs: (filters) => ['assessmentCatalogs', filters],
  assessmentCatalog: (id) => ['assessmentCatalogs', id],

  // Scheduling
  schedules: (filters) => ['schedules', filters],
  schedule: (id) => ['schedules', id],
  availability: (staffId) => ['availability', staffId],

  // Billing
  invoices: (filters) => ['invoices', filters],
  invoice: (id) => ['invoices', id],
  claims: (filters) => ['claims', filters],
  claim: (id) => ['claims', id],

  // HR / Staff
  staff: (filters) => ['staff', filters],
  staffMember: (id) => ['staff', id],

  // Documents
  documents: (filters) => ['documents', filters],
  document: (id) => ['documents', id],

  // Notifications
  notifications: (filters) => ['notifications', filters],
  unreadNotifications: ['notifications', 'unread'],

  // Analytics & Reports
  dashboardStats: (filters) => ['analytics', 'dashboard', filters],
  clientProgress: (clientId) => ['analytics', 'client', clientId, 'progress'],
  programAnalytics: (programId) => ['analytics', 'program', programId],

  // Roles & Permissions (from public schema)
  roles: ['roles'],
  role: (id) => ['roles', id],
  permissions: ['permissions']
};

/**
 * Invalidate queries helper
 * Use this to invalidate related queries after mutations
 */
export const invalidateQueries = async (queryClient, keys) => {
  const promises = keys.map((key) => queryClient.invalidateQueries({ queryKey: key }));
  await Promise.all(promises);
};

export default getQueryClient;
